import { useContext, useMemo } from 'react'
import { AuthContext, type AuthState } from './AuthProvider'
import type { Permission, SpaceMembership } from './permissions'

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth muss innerhalb von <AuthProvider> stehen')
  }
  return ctx
}

/**
 * Darf der angemeldete Nutzer das?
 *
 * KEINE SICHERHEITSGRENZE. Diese Funktion blendet Bedienelemente aus, die der
 * Server ohnehin ablehnen wuerde. Durchgesetzt wird in Postgres per RLS.
 *
 * Ohne `spaceId` wird gegen die globalen Rechte geprueft. Mit `spaceId`
 * zusaetzlich gegen die Rolle in diesem Bereich — ein globales Recht gilt
 * ueberall und schlaegt durch.
 */
export function useCan(permission: Permission, spaceId?: string): boolean {
  const { permissions, spaces } = useAuth()

  return useMemo(() => {
    if (permissions.has(permission)) return true
    if (!spaceId) return false

    const space = spaces.find((s) => s.id === spaceId || s.slug === spaceId)
    return space?.permissions.includes(permission) ?? false
  }, [permissions, spaces, permission, spaceId])
}

/** Mehrere Rechte auf einmal — spart Hook-Aufrufe in Listen und Menues. */
export function useCanAll(permissions: Permission[], spaceId?: string): boolean {
  const { permissions: granted, spaces } = useAuth()

  return useMemo(() => {
    const space = spaceId
      ? spaces.find((s) => s.id === spaceId || s.slug === spaceId)
      : undefined

    return permissions.every(
      (p) => granted.has(p) || (space?.permissions.includes(p) ?? false),
    )
  }, [granted, spaces, permissions, spaceId])
}

/** Der Bereich mit den effektiven Rechten des Nutzers, oder undefined. */
export function useSpace(idOrSlug: string | undefined): SpaceMembership | undefined {
  const { spaces } = useAuth()
  return useMemo(
    () =>
      idOrSlug
        ? spaces.find((s) => s.id === idOrSlug || s.slug === idOrSlug)
        : undefined,
    [spaces, idOrSlug],
  )
}
